import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Snackbar from "@mui/material/Snackbar";

// Components
import Chat from "../components/Chat";
import EmptyState from "./EmptyState";
import SnapshotChatTop from "./ChatTop";
import DraggingOverlay from "./DraggingOverlay";

// Styles
import styles from "./style.module.scss";
import Typography from "../components/Typography";
import SnackbarContent from "@mui/material/SnackbarContent";
import Alert from "@mui/material/Alert";

const SnapshotView: React.FC = () => {
  // Hooks
  const { t } = useTranslation();

  // States
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [userAddedFiles, setUserAddedFiles] = useState<File[]>([]);
  const [ignoredFiles, setIgnoredFiles] = useState<File[]>([]);


  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      const files = Array.from(event.dataTransfer.files);
      const acceptedFiles = []
      const ignoredFiles = []

      for (const file of files) {
        const { type, name } = file;
        const ext = name.split('.').pop()?.toLowerCase();
        // PDF
        if (type === "application/pdf" || ext === "pdf") {
          acceptedFiles.push(file);
          continue;
        }
        // Image
        if (type.startsWith("image/")) {
          acceptedFiles.push(file);
          continue;
        }
        // Word Docs
        if (
          type === "application/msword" ||
          type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          ext === "doc" ||
          ext === "docx"
        ) {
          acceptedFiles.push(file);
          continue;
        }
        // Text Files
        if (ext === "txt") {
          acceptedFiles.push(file);
          continue;
        }

        ignoredFiles.push(file);
      }

      setUserAddedFiles(acceptedFiles);
      setIgnoredFiles(ignoredFiles);
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }

  return (
    <div className={styles.root}>
      <Snackbar
        open={ignoredFiles.length > 0}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setIgnoredFiles([])}
      >
        <Alert severity="warning" variant="standard" className={styles.alert}>
          <div className={styles.alert_container}>
            <Typography variant="p" className={styles.alert_text}>
              {t("generate.snapshot.alert")}
            </Typography>
            <Typography variant="p" className={styles.alert_text}>
              {t("generate.snapshot.ignored_files")}
            </Typography>
            <div className={styles.ignored_files}>
              {ignoredFiles.map((file) => (
                <Typography variant="p" key={file.name} className={styles.alert_text}>
                  {file.name}
                </Typography>
              ))}
            </div>
          </div>
        </Alert>
      </Snackbar>

      <div
        className={styles.chat_container}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        {isDragging && <DraggingOverlay />}

        <Chat
          passedFiles={userAddedFiles}
          onTopOfChat={() => <SnapshotChatTop />}
          onRenderEmptyChat={() => <EmptyState />}
        />
      </div>
    </div>
  )
};

export default SnapshotView;
